const API_BASE_URL = process.env.REACT_APP_API_URL ;

class ApiService {
    constructor() {
        this.token = localStorage.getItem('token');
        this.user = JSON.parse(localStorage.getItem('user') || '{}');
        this.permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
    }

    async request(endpoint, options = {}) {
        const url = `${API_BASE_URL}/api${endpoint}`;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(this.token && { Authorization: `Bearer ${this.token}` })
            },
            credentials: 'include',
            ...options,
        };
        
        if (config.body && typeof config.body === 'object') {
            config.body = JSON.stringify(config.body);
        }

        if (process.env.NODE_ENV !== 'production') {
            console.log('[FETCH]', url, options.method || 'GET');
        }

        try {
            const response = await fetch(url, config);
            const text = await response.text();

            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error('Invalid JSON from server: ' + text);
            }

            if (!response.ok) {
                if (response.status === 401) {
                    this.logout();
                    window.location.href = '/';
                    throw new Error('Session expired. Please login again.');
                }
                throw new Error(data.error || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    // Auth API
    async login(credentials) {
        try {
            const response = await this.request('/login', {
                method: 'POST',
                body: credentials
            });
            
            if (response.token) {
                this.token = response.token;
                this.user = response.user;
                this.permissions = response.permissions;
                
                localStorage.setItem('token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));
                localStorage.setItem('permissions', JSON.stringify(response.permissions));
            }
            
            return response;
        } catch (error) {
            throw error;
        }
    }

    async logout() {
        try {
            if (this.token) {
                await this.request('/logout', { method: 'POST' });
            }
        } catch (error) {
            console.error('Logout request failed:', error);
        } finally {
            this.token = null;
            this.user = {};
            this.permissions = {};
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');
        }
    }

    async verifyToken() {
        try {
            const response = await this.request('/verify');
            if (response.user) {
                this.user = response.user;
                this.permissions = response.permissions;
                localStorage.setItem('user', JSON.stringify(response.user));
                localStorage.setItem('permissions', JSON.stringify(response.permissions));
            }
            return response;
        } catch (error) {
            this.logout();
            throw error;
        }
    }

    // Permission checks
    isAuthenticated() {
        return !!this.token;
    }

    hasRole(role) {
        return this.user.role === role;
    }

    hasPermission(section, item) {
        if (this.user.role === 'owner') return true;
        return this.permissions[section]?.includes(item) || false;
    }

    canAccessPortfolio(item) {
        return this.hasPermission('portfolio', item);
    }

    canAccessBilling(item) {
        return this.hasPermission('billing', item);
    }

    canAccessProduce(item) {
        return this.hasPermission('produce', item);
    }

    // Employee API
    async getEmployees() {
        return this.request('/employees');
    }

    async createEmployee(data) {
        return this.request('/employees', {
            method: 'POST',
            body: data,
        });
    }

    async updateEmployee(id, data) {
        return this.request(`/employees/${id}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteEmployee(id) {
        return this.request(`/employees/${id}`, {
            method: 'DELETE',
        });
    }

    // Tabela API
    async getTabela() {
        return this.request('/tabela');
    }

    async createTabela(data) {
        return this.request('/tabela', {
            method: 'POST',
            body: data,
        });
    }

    async updateTabela(id, data) {
        return this.request(`/tabela/${id}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteTabela(id) {
        return this.request(`/tabela/${id}`, {
            method: 'DELETE',
        });
    }

    async getTabelaMilkCollection(tabelaId, params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        
        const queryString = queryParams.toString();
        const endpoint = `/tabela/${tabelaId}/milk-collection${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    // Khata API
    async getKhata() {
        return this.request('/khata');
    }

    async createKhata(data) {
        return this.request('/khata', {
            method: 'POST',
            body: data,
        });
    }

    async updateKhata(tabelaId, khataNo, data) {
        return this.request(`/khata/${tabelaId}/${khataNo}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteKhata(tabelaId, khataNo) {
        return this.request(`/khata/${tabelaId}/${khataNo}`, {
            method: 'DELETE',
        });
    }

    async getKhataMilkCollection(khataId, params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        
        const queryString = queryParams.toString();
        const endpoint = `/khata/${khataId}/milk-collection${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    // Animals API
    async getAnimals() {
        return this.request('/animals');
    }

    async createAnimal(data) {
        return this.request('/animals', {
            method: 'POST',
            body: data,
        });
    }

    async updateAnimal(tagNumber, data) {
        return this.request(`/animals/${tagNumber}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteAnimal(tagNumber) {
        return this.request(`/animals/${tagNumber}`, {
            method: 'DELETE',
        });
    }

    async retireAnimal(tagNumber, data) {
        return this.request(`/animals/${tagNumber}/retire`, {
            method: 'POST',
            body: data,
        });
    }

    // Customers API
    async getCustomers() {
        return this.request('/customers');
    }

    async createCustomer(data) {
        return this.request('/customers', {
            method: 'POST',
            body: data,
        });
    }

    async updateCustomer(id, data) {
        return this.request(`/customers/${id}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteCustomer(id) {
        return this.request(`/customers/${id}`, {
            method: 'DELETE',
        });
    }

    async getCustomerSummary(customerId) {
        return this.request(`/customers/${customerId}/summary`);
    }

    async getCustomerBills(customerId, params = {}) {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        
        const queryString = queryParams.toString();
        const endpoint = `/customers/${customerId}/bills${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    async getCustomerBankDetails(customerId) {
        return this.request(`/customers/${customerId}/bank-details`);
    }

    async addCustomerBankDetail(customerId, data) {
        return this.request(`/customers/${customerId}/bank-details`, {
            method: 'POST',
            body: data,
        });
    }

    async updateCustomerBankDetail(customerId, bankDetailId, data) {
        return this.request(`/customers/${customerId}/bank-details/${bankDetailId}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteCustomerBankDetail(customerId, bankDetailId) {
        return this.request(`/customers/${customerId}/bank-details/${bankDetailId}`, {
            method: 'DELETE',
        });
    }

    async setPrimaryBankDetail(customerId, bankDetailId) {
        return this.request(`/customers/${customerId}/bank-details/${bankDetailId}/set-primary`, {
            method: 'PUT',
        });
    }

    // Produce / Daily Milk API
    async getDailyMilkCollection(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.khata_id) queryParams.append('khata_id', params.khata_id);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        
        const queryString = queryParams.toString();
        const endpoint = `/daily-milk${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    async createDailyMilkRecord(data) {
        return this.request('/daily-milk', {
            method: 'POST',
            body: data,
        });
    }

    async updateDailyMilkRecord(recordId, data) {
        return this.request(`/daily-milk/${recordId}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteDailyMilkRecord(recordId) {
        return this.request(`/daily-milk/${recordId}`, {
            method: 'DELETE',
        });
    }

    // Bills API
    async searchBills(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.customer_id) queryParams.append('customer_id', params.customer_id);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.min_amount) queryParams.append('min_amount', params.min_amount);
        if (params.max_amount) queryParams.append('max_amount', params.max_amount);
        if (params.sort_order) queryParams.append('sort_order', params.sort_order);
        if (params.status) queryParams.append('status', params.status);
        if (params.vehicle_number) queryParams.append('vehicle_number', params.vehicle_number);

        const queryString = queryParams.toString();
        const endpoint = `/bills/search${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    async getBills(params = {}) {
        if (Object.keys(params).length === 0) {
            return this.request('/bills/daily?page=1&per_page=200');
        }
        return this.searchBills(params);
    }

    async createBill(data) {
        return this.request('/bills', {
            method: 'POST',
            body: data,
        });
    }

    async replicateBillRange(data) {
        return this.request('/bills/replicate', {
            method: 'POST',
            body: data,
        });
    }

    async updateBill(billNo, data) {
        return this.request(`/bills/${billNo}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteBill(billNo) {
        return this.request(`/bills/${billNo}`, {
            method: 'DELETE',
        });
    }

    async bulkDeleteBills(billNos) {
        return this.request('/bills/bulk-delete', {
            method: 'POST',
            body: { bill_nos: billNos },
        });
    }

    // ─── Vehicle-based bulk sale entry ───────────────────────────────────────────

    async getVehicleEntry(params = {}) {
        const qs = new URLSearchParams();
        if (params.vehicle_number) qs.append('vehicle_number', params.vehicle_number);
        if (params.date) qs.append('date', params.date);
        if (params.slot) qs.append('slot', params.slot);
        return this.request(`/bills/vehicle-entry${qs.toString() ? `?${qs}` : ''}`);
    }

    async bulkCreateVehicleBills(data) {
        return this.request('/bills/bulk-vehicle', {
            method: 'POST',
            body: data,
        });
    }

    // Payment API
    async getCustomerPayments(customerId, params = {}) {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.sort_order) queryParams.append('sort_order', params.sort_order);

        const queryString = queryParams.toString();
        const endpoint = `/customers/${customerId}/payments${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    async addCustomerPayment(customerId, data) {
        return this.request(`/customers/${customerId}/payments`, {
            method: 'POST',
            body: data,
        });
    }

    // ─── Dairy Farm API ──────────────────────────────────────────────────────────

    async getDairyFarms() {
        return this.request('/dairy-farms');
    }

    async createDairyFarm(data) {
        return this.request('/dairy-farms', { method: 'POST', body: data });
    }

    async updateDairyFarm(farmId, data) {
        return this.request(`/dairy-farms/${farmId}`, { method: 'PUT', body: data });
    }

    async getDairyFarmOverview(farmId, params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        if (params.customer_id) qs.append('customer_id', params.customer_id);
        return this.request(`/dairy-farms/${farmId}/overview${qs.toString() ? `?${qs}` : ''}`);
    }

    async getUnassignedFarmOverview(params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        if (params.customer_id) qs.append('customer_id', params.customer_id);
        return this.request(`/dairy-farms/unassigned-overview${qs.toString() ? `?${qs}` : ''}`);
    }

    async getAllFarmsOverview(params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        if (params.customer_id) qs.append('customer_id', params.customer_id);
        return this.request(`/dairy-farms/all-overview${qs.toString() ? `?${qs}` : ''}`);
    }

    async getNextInvoiceNumber(dairyFarmId) {
        const qs = new URLSearchParams();
        if (dairyFarmId) qs.append('dairy_farm_id', dairyFarmId);
        return this.request(`/invoices/next-number?${qs}`);
    }

    async deleteDairyFarm(farmId) {
        return this.request(`/dairy-farms/${farmId}`, { method: 'DELETE' });
    }

    // ─── Animal Weekly/Daily Milk API ────────────────────────────────────────────
    // FIXED: added record_type param support + added missing update/delete methods

    async getAnimalWeeklyMilk(tagNumber, params = {}) {
        const queryParams = new URLSearchParams();
        if (params.weeks_back) queryParams.append('weeks_back', params.weeks_back);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        // Pass record_type filter so backend only returns daily records when requested
        if (params.record_type) queryParams.append('record_type', params.record_type);
        
        const queryString = queryParams.toString();
        const endpoint = `/animals/${tagNumber}/weekly-milk${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }
    
    async createAnimalWeeklyMilk(tagNumber, data) {
        return this.request(`/animals/${tagNumber}/weekly-milk`, {
            method: 'POST',
            body: data,
        });
    }

    // ADDED: was missing entirely — caused silent failures on edit
    async updateAnimalWeeklyMilk(tagNumber, recordId, data) {
        return this.request(`/animals/${tagNumber}/weekly-milk/${recordId}`, {
            method: 'PUT',
            body: data,
        });
    }

    // ADDED: was missing entirely — caused silent failures on delete
    async deleteAnimalWeeklyMilk(tagNumber, recordId) {
        return this.request(`/animals/${tagNumber}/weekly-milk/${recordId}`, {
            method: 'DELETE',
        });
    }

    // ─── Milk Purchase API ───────────────────────────────────────────────────────

    async getMilkPurchases(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.supplier_name) queryParams.append('supplier_name', params.supplier_name);
        
        const queryString = queryParams.toString();
        const endpoint = `/milk-purchases${queryString ? `?${queryString}` : ''}`;
        
        const response = await this.request(endpoint);
        return {
            purchases: response.data,
            total: response.pagination.total,
            pages: response.pagination.pages,
            current_page: response.pagination.page,
            per_page: response.pagination.per_page
        };
    }

    async createMilkPurchase(data) {
        const response = await this.request('/milk-purchases', {
            method: 'POST',
            body: data,
        });
        return {
            message: response.message,
            purchase: response.data
        };
    }

    async updateMilkPurchase(purchaseId, data) {
        const response = await this.request(`/milk-purchases/${purchaseId}`, {
            method: 'PUT',
            body: data,
        });
        return {
            message: response.message,
            purchase: response.data
        };
    }

    async deleteMilkPurchase(purchaseId) {
        return this.request(`/milk-purchases/${purchaseId}`, {
            method: 'DELETE',
        });
    }

    async getMilkPurchaseSummary(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        
        const queryString = queryParams.toString();
        const endpoint = `/milk-purchases/summary${queryString ? `?${queryString}` : ''}`;
        
        const response = await this.request(endpoint);
        return response.statistics;
    }

    async searchMilkPurchases(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.supplier_name) queryParams.append('supplier_name', params.supplier_name);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.min_amount) queryParams.append('min_amount', params.min_amount);
        if (params.max_amount) queryParams.append('max_amount', params.max_amount);
        
        const queryString = queryParams.toString();
        const endpoint = `/milk-purchases/search${queryString ? `?${queryString}` : ''}`;
        
        const response = await this.request(endpoint);
        return {
            purchases: response.data,
            total: response.pagination.total,
            pages: response.pagination.pages,
            current_page: response.pagination.page,
            per_page: response.pagination.per_page
        };
    }

    // ─── Goods Purchase API ──────────────────────────────────────────────────────

    async getGoodsPurchases(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.supplier_name) queryParams.append('supplier_name', params.supplier_name);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        
        const queryString = queryParams.toString();
        const endpoint = `/goods-purchases${queryString ? `?${queryString}` : ''}`;
        
        const response = await this.request(endpoint);
        return {
            purchases: response.data,
            total: response.pagination?.total || 0,
            pages: response.pagination?.pages || 1,
            current_page: response.pagination?.page || 1,
            per_page: response.pagination?.per_page || 20
        };
    }

    async getGoodsPurchaseSummary(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        
        const queryString = queryParams.toString();
        const endpoint = `/goods-purchases/summary${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    async createGoodsPurchase(data) {
        return this.request('/goods-purchases', {
            method: 'POST',
            body: data,
        });
    }

    async updateGoodsPurchase(id, data) {
        return this.request(`/goods-purchases/${id}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteGoodsPurchase(id) {
        return this.request(`/goods-purchases/${id}`, {
            method: 'DELETE',
        });
    }

    // ─── Supplier Payment API ────────────────────────────────────────────────────

    async getSupplierPayments(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.supplier_name) queryParams.append('supplier_name', params.supplier_name);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.payment_mode) queryParams.append('payment_mode', params.payment_mode);
        
        const queryString = queryParams.toString();
        const endpoint = `/supplier-payments${queryString ? `?${queryString}` : ''}`;
        return this.request(endpoint);
    }

    async createSupplierPayment(data) {
        return this.request('/supplier-payments', {
            method: 'POST',
            body: data,
        });
    }

    async updateSupplierPayment(id, data) {
        return this.request(`/supplier-payments/${id}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteSupplierPayment(id) {
        return this.request(`/supplier-payments/${id}`, {
            method: 'DELETE',
        });
    }

    async getVendorOpenBills(vendorId) {
        return this.request(`/supplier-payments/vendor/${vendorId}/open-bills`);
    }

    // ─── Product Items API (Settings) ───────────────────────────────────────────

    async getProductItems() {
        return this.request('/product-items');
    }

    async createProductItem(data) {
        return this.request('/product-items', {
            method: 'POST',
            body: data,
        });
    }

    async updateProductItem(itemId, data) {
        return this.request(`/product-items/${itemId}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteProductItem(itemId) {
        return this.request(`/product-items/${itemId}`, {
            method: 'DELETE',
        });
    }

    // ─── Animal Purchase API ─────────────────────────────────────────────────────

    async getAnimalPurchases(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        if (params.start_date) queryParams.append('start_date', params.start_date);
        if (params.end_date) queryParams.append('end_date', params.end_date);
        if (params.bought_from) queryParams.append('bought_from', params.bought_from);

        const qs = queryParams.toString();
        return this.request(`/animal-purchases${qs ? `?${qs}` : ''}`);
    }

    async createAnimalPurchase(data) {
        return this.request('/animal-purchases', {
            method: 'POST',
            body: data,
        });
    }

    async updateAnimalPurchase(id, data) {
        return this.request(`/animal-purchases/${id}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteAnimalPurchase(id) {
        return this.request(`/animal-purchases/${id}`, {
            method: 'DELETE',
        });
    }

    // ─── Vendor API ──────────────────────────────────────────────────────────────

    async getVendors(params = {}) {
        const qs = new URLSearchParams();
        if (params.vendor_type) qs.append('vendor_type', params.vendor_type);
        return this.request(`/vendors${qs.toString() ? `?${qs}` : ''}`);
    }

    async getVendor(id) {
        return this.request(`/vendors/${id}`);
    }

    async getVendorSummary(id) {
        return this.request(`/vendors/${id}/summary`);
    }

    async createVendor(data) {
        return this.request('/vendors', { method: 'POST', body: data });
    }

    async updateVendor(id, data) {
        return this.request(`/vendors/${id}`, { method: 'PUT', body: data });
    }

    async deleteVendor(id) {
        return this.request(`/vendors/${id}`, { method: 'DELETE' });
    }

    // ─── Invoice API ─────────────────────────────────────────────────────────────

    async getInvoices(params = {}) {
        const qs = new URLSearchParams();
        if (params.customer_id) qs.append('customer_id', params.customer_id);
        if (params.dairy_farm_id) qs.append('dairy_farm_id', params.dairy_farm_id);
        if (params.status) qs.append('status', params.status);
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        return this.request(`/invoices${qs.toString() ? `?${qs}` : ''}`);
    }

    async getInvoice(id) {
        return this.request(`/invoices/${id}`);
    }

    async createInvoice(data) {
        return this.request('/invoices', { method: 'POST', body: data });
    }

    async updateInvoice(id, data) {
        return this.request(`/invoices/${id}`, { method: 'PUT', body: data });
    }

    async deleteInvoice(id) {
        return this.request(`/invoices/${id}`, { method: 'DELETE' });
    }

    async getUnbilledBills(customerId, params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        return this.request(`/invoices/customer/${customerId}/unbilled${qs.toString() ? `?${qs}` : ''}`);
    }

    // ─── Customer Payment update/delete ──────────────────────────────────────────

    async updateCustomerPayment(customerId, paymentId, data) {
        return this.request(`/customers/${customerId}/payments/${paymentId}`, {
            method: 'PUT',
            body: data,
        });
    }

    async deleteCustomerPayment(customerId, paymentId) {
        return this.request(`/customers/${customerId}/payments/${paymentId}`, {
            method: 'DELETE',
        });
    }

    async bulkDeletePayments(paymentIds) {
        return this.request('/payments/bulk-delete', {
            method: 'POST',
            body: { payment_ids: paymentIds },
        });
    }

    // ─── Customer Party Ledger ────────────────────────────────────────────────────

    async getCustomerLedger(customerId, params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        if (params.dairy_farm_id) qs.append('dairy_farm_id', params.dairy_farm_id);
        if (params.unassigned_farm) qs.append('unassigned_farm', 'true');
        return this.request(`/customers/${customerId}/ledger${qs.toString() ? `?${qs}` : ''}`);
    }

    // ─── Vendor Party Ledger ──────────────────────────────────────────────────────

    async getVendorLedger(vendorId, params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        return this.request(`/vendors/${vendorId}/ledger${qs.toString() ? `?${qs}` : ''}`);
    }

    // ─── Cash book / Bank statement / Journal / Owner dashboard ──────────────

    async getCashbook(params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        if (params.party) qs.append('party', params.party);
        return this.request(`/cashbook${qs.toString() ? `?${qs}` : ''}`);
    }

    async getBankbook(params = {}) {
        const qs = new URLSearchParams();
        if (params.start_date) qs.append('start_date', params.start_date);
        if (params.end_date) qs.append('end_date', params.end_date);
        if (params.party) qs.append('party', params.party);
        return this.request(`/bankbook${qs.toString() ? `?${qs}` : ''}`);
    }

    async getOwnerDashboard(params = {}) {
        const qs = new URLSearchParams();
        if (params.filter) qs.append('filter', params.filter);
        return this.request(`/dashboard/owner${qs.toString() ? `?${qs}` : ''}`);
    }

    async getCustomerCredit(customerId) {
        return this.request(`/customers/${customerId}/credit`);
    }

    async getVendorCredit(vendorId) {
        return this.request(`/vendors/${vendorId}/credit`);
    }
}

if (process.env.NODE_ENV !== 'production') {
    console.log('API_BASE_URL:', API_BASE_URL);
}
const apiService = new ApiService();
export default apiService;